/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/*
  In this files we configured router and menubar details
  #MenuLayout     : In this menulayout we mapped all our 
  #BrowserRouter  : BrowserRouter is a router implementation that uses the HTML5 history API (pushstate, replacestate, and popstate events) to keep your UI in sync with the URL.
                    It is the parent component used to store all other components.
  #RecoilRoot     : Recoil used to manage the global state value(s)
*/
// System defined variables
import React, { useState } from 'react'
import { ConfirmProvider } from 'material-ui-confirm'
import { useLocation } from 'react-router-dom'
import {
  AuthenticatedTemplate,
  MsalAuthenticationTemplate,
  useMsal,
} from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil'
import jwtDecode from 'jwt-decode'

// Custom defined variables
import { loginRequest } from '../configs/msalAuthConfigs'
import RoutesFile from '../views/routes/Routes'
import BackdropSpinner from '../views/components/base/backdropSpinner/BackdropSpinner'
import Toast from '../views/components/base/toast/Toast'
import MenuLayout from '../views/components/custom/menuLayout/MenuLayout'
import ErrorBoundary from '../views/components/custom/errorBoundary/ErrorBoundary'
// Loading component is used to pass as loading component for msalAuthentication
import TitleBar from '../views/pages/titleBar/TitleBar'
import {
  showSplashScreen,
  userTokenDetails,
  toastMessage,
  mappedUserRoles,
  assignedContractRoles,
  assignedDistinctRoles,
  userDetail,
  runtimeTokenExpiredAtom,
  errorCodes,
  gotInitialSessionResponseOfAuthenticationAtom,
  axiosConfiguration,
  selectedMenuData,
  airaLayoutChanges,
  hideTitleBarAtom,
  themeModeAtom,
} from '../views/components/recoil/atoms/atoms'
import UserPermission from '../views/pages/userPermission/UserPermission'
import apiEndPoints from '../../src/models/api/apiEndpoints'
import { fetchDataSelector } from '../views/components/recoil/selectors/selectors'
import utils from '../../src/utils/utils'
import { authenticationData } from '../views/components/recoil/atoms/atomsWithLocalStorage'
import IdleStateDetector from '../views/components/custom/idleStateDetector/IdleStateDetector'
import ResponseErrorCode from '../views/pages/responseErrorCode/ResponseErrorCode'
import './App.css'
import 'react-web-white-label/dist/app/global.css'
import {
  applyTheme,
  initializeTheme,
  THEME_DARK,
  THEME_LIGHT,
} from './theme/ThemeModeManager'

function App({ msalInstance, loginHintUserName }) {
  const setAuthenticationResult = useSetRecoilState(authenticationData)
  const setMappedUserRoles = useSetRecoilState(mappedUserRoles)
  const setAssignedContractRoles = useSetRecoilState(assignedContractRoles)
  const setAssignedDistinctRoles = useSetRecoilState(assignedDistinctRoles)
  const [loggedUserDetail, setLoggedInUserDetail] = useRecoilState(userDetail)

  const authenticatedUserData = useRecoilValue(authenticationData)
  const getCoreData = useRecoilValue(fetchDataSelector)
  const getSelectedMenuData = useRecoilValue(selectedMenuData)
  const [runtimeTokenExpired, setRuntimeTokenExpired] = useRecoilState(
    runtimeTokenExpiredAtom
  )
  const gotInitialSessionResponseOfAuthentication = useRecoilValue(
    gotInitialSessionResponseOfAuthenticationAtom
  )
  const [userPermissionCheckCall, setUserPermissionCheckCall] = useState(false)
  const [permittedUser, setPermittedUser] = useState(null)
  const [accountPermissionHint, setAccountPermissionHint] = useState(
    'Insufficient Permissions'
  )
  const showToastMessage = useSetRecoilState(toastMessage)
  // For authenticated user Splash screen will be shown once
  const setShowSplashScreen = useSetRecoilState(showSplashScreen)
  const setUserTokenDetails = useSetRecoilState(userTokenDetails)
  const responseErrorCodes = useRecoilValue(errorCodes)
  const [refreshLoginApi, setRefreshLoginApi] = useState(false)
  const configVariable = useRecoilValue(axiosConfiguration)
  const isAiraLayoutChanges = useRecoilValue(airaLayoutChanges)
  const hideTitleBar = useRecoilValue(hideTitleBarAtom)
  const [themeMode, setThemeMode] = useRecoilState(themeModeAtom)
  // Commenting below, due to instead of isAuthenticated from msal using local isAuthenticatedSilentFlag to check whether user has logged in or not
  // const isAuthenticated = useIsAuthenticated()
  const { instance } = useMsal()
  const location = useLocation()

  React.useEffect(() => {
    const initialTheme = initializeTheme()
    setThemeMode(initialTheme)
  }, [setThemeMode])
  React.useEffect(() => {
    applyTheme(themeMode || THEME_LIGHT)
  }, [themeMode])
  React.useEffect(() => {
    if (!loggedUserDetail) return
    const loadThemePreference = async () => {
      try {
        const response = await getCoreData(
          apiEndPoints?.UserPreference?.getDarkMode?.method,
          apiEndPoints?.UserPreference?.getDarkMode?.url
        )
        if (
          response?.status === utils?.apiResponseCode?.successCode &&
          typeof response?.data?.isDarkMode === 'boolean'
        ) {
          const preferredTheme = response.data.isDarkMode
            ? THEME_DARK
            : THEME_LIGHT
          setThemeMode(preferredTheme)
          applyTheme(preferredTheme)
        }
      } catch (_) {
        // fall back to localStorage-based theme
      }
    }
    loadThemePreference()
  }, [loggedUserDetail])

  React.useEffect(() => {
    // added event listsner function to set show splash screen to false before refresh and the same will persis and splash screen will not be shown after page refresh
    window?.addEventListener('beforeunload', () => {
      setShowSplashScreen(false)
    })
  }, [])
  const availableRoleValidation = (roleData) => {
    if (typeof roleData === 'object') {
      roleData?.forEach((roleName) => {
        if (!utils?.userRoleCheck?.includes(roleName)) {
          return false
        }
      })
    } else {
      if (!utils?.userRoleCheck?.includes(roleData)) {
        return false
      }
    }
    return true
  }

  const getAiraManagerLoginTime = async () => {
    const postData = {
      isLoggedIn: true,
    }

    getCoreData(
      apiEndPoints?.postAIRAManagerloginTime?.method,
      `${apiEndPoints?.postAIRAManagerloginTime?.url}`,
      postData
    )
      .then((response) => {
        if (response?.status === utils?.apiResponseCode?.successCode) {
          // console.log('Response:', response)
        }
      })
      .catch(() => {
        // console.log('Error in update login API')
      })
  }
  React.useEffect(() => {
    if (!loggedUserDetail || !authenticatedUserData) return
    // Skip if token is expired — will re-run after token refresh updates authenticatedUserData
    const exp = authenticatedUserData?.idTokenClaims?.exp
    if (exp && exp * 1000 < Date.now()) return
    // As per Bug 136801: Invalid Data displayed in the Usage Report - The last login details required for all roles and users. So we removed the role validation

    getAiraManagerLoginTime()
  }, [loggedUserDetail, authenticatedUserData])
  async function fetchUserTokenDetails() {
    Promise.all([
      await getCoreData(
        apiEndPoints?.userPermissionCheck?.method,
        `${apiEndPoints?.userPermissionCheck?.url}`,
        '',
        { Authorization: 'Bearer' + ' ' + authenticatedUserData?.idToken },
        null,
        true,
        'usePermission'
      ),
    ]).then(([response]) => {
      if (response?.status === utils?.apiResponseCode?.successCode) {
        var token = response?.data?.token
        var decoded = jwtDecode(token)
        setLoggedInUserDetail(decoded)
        // will get distinct roles and roles for assigned each contract from this decode
        if (availableRoleValidation(decoded?.role)) {
          // initially set distinct roles to mappedUserRole. Will change this based on contract once clicked any contract widget.
          if (typeof decoded?.role === 'string') {
            setMappedUserRoles([decoded?.role])
            setAssignedDistinctRoles([decoded?.role])
          } else {
            setMappedUserRoles(decoded?.role)
            setAssignedDistinctRoles(decoded?.role)
          }
          // will have the JSON of assigned contracts with contractId,name and role
          setAssignedContractRoles(decoded?.contract)

          setUserTokenDetails(response?.data)
          setPermittedUser(true)
        } else {
          setAccountPermissionHint('Insufficient Permissions')
          setPermittedUser(false)
        }
      } else if (response?.data?.validationErrors?.length > 0) {
        if (
          response?.data?.validationErrors[0]?.errorMessage
            .toLocaleLowerCase()
            .includes('insufficient')
        ) {
          setAccountPermissionHint(
            utils?.userPermissionMessage?.insufficientPermission
          )
        } else if (
          response?.data?.validationErrors[0]?.errorMessage
            .toLocaleLowerCase()
            .includes('account')
        ) {
          setAccountPermissionHint(
            utils?.userPermissionMessage?.accountInactive
          )
        } else {
          setAccountPermissionHint(
            response?.data?.validationErrors[0]?.errorMessage
          )
        }
        setPermittedUser(false)
      } else if (response?.data) {
        setPermittedUser(false)
        showToastMessage({
          message: response.data?.errors,
          status: 'error',
        })
      }
      //setPermittedUser(true)
      setUserPermissionCheckCall(true)
      setRefreshLoginApi(false)
    })
  }
  //User permission check and validation
  React.useEffect(() => {
    if (
      authenticatedUserData &&
      gotInitialSessionResponseOfAuthentication &&
      !userPermissionCheckCall &&
      !runtimeTokenExpired
    ) {
      fetchUserTokenDetails()
    } else if (runtimeTokenExpired) {
      setUserPermissionCheckCall(true)
    }
  }, [
    authenticatedUserData,
    gotInitialSessionResponseOfAuthentication,
    runtimeTokenExpired,
  ])
  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  React.useEffect(() => {
    if (refreshLoginApi) {
      fetchUserTokenDetails()
    }
  }, [refreshLoginApi])

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  const getNewTokenForLoggedInUser = () => {
    const accessTokenRequest = {
      scopes: ['openid'],
      account: authenticatedUserData?.account,
      forceRefresh: true,
      tokenQueryParameters: { ...authenticatedUserData },
    }

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((response) => {
        setAuthenticationResult(response)
        setRefreshLoginApi(true)
      })
      .catch(() => {})
  }

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  const getWhenToRefreshTokenInMillSeconds = () => {
    const expiresOn = new Date(authenticatedUserData?.idTokenClaims?.exp * 1000) //authenticatedUserData?.idTokenClaims?.exp - this will have timeStamp. So multiple * 1000
    const now = new Date(utils?.returnCurrentDateTimeInUTC())

    const diffInMinutes = (expiresOn - now) / 1000 / 60

    let executeAfter = 0
    // if (diffInMinutes >= utils?.refreshTokenMinutesEarlier) {
    executeAfter = (diffInMinutes - utils?.refreshTokenMinutesEarlier) * 60000
    // }
    return executeAfter
  }

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  React.useEffect(() => {
    if (authenticatedUserData) {
      const executeAfter = getWhenToRefreshTokenInMillSeconds()
      if (executeAfter <= 0) {
        // Token already expired or about to — refresh immediately before any API call
        getNewTokenForLoggedInUser()
      } else {
        setTimeout(() => {
          getNewTokenForLoggedInUser()
        }, [executeAfter])
      }
    }
  }, [authenticatedUserData])

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  React.useEffect(() => {
    if (runtimeTokenExpired) {
      setAccountPermissionHint(utils?.tokenExpiredMessage)
    }
  }, [runtimeTokenExpired])

  const bodyStyles = document.body.style

  const ApplicationInsightsModuleLogValue =
    configVariable?.VariableSettings?.ApplicationInsightsModuleLogValue
  localStorage.setItem(
    'ApplicationInsightsModuleLogValue',
    ApplicationInsightsModuleLogValue
  )

  const AppInsightsKey = configVariable?.VariableSettings?.AppInsightsKey
  localStorage.setItem('AppInsightsKey', AppInsightsKey)

  return (
    <ErrorBoundary>
      <ConfirmProvider>
        <BackdropSpinner />
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={{
            ...loginRequest,
            loginHint: loginHintUserName,
          }}
        >
          <AuthenticatedTemplate>
            <>
              {responseErrorCodes &&
              responseErrorCodes?.showErrorPage === true ? (
                <ResponseErrorCode
                  responseErrorCode={responseErrorCodes?.errorCode}
                />
              ) : !userPermissionCheckCall ? (
                <></>
              ) : permittedUser && !runtimeTokenExpired ? (
                <>
                  <Toast />

                  <div
                    className={`boApplication ${
                      utils?.hideTitleBarForPaths?.includes(
                        location?.pathname?.toString()?.toLowerCase()
                      ) || hideTitleBar
                        ? 'hide-title-bar'
                        : ''
                    } ${
                      utils?.reportSubMenuPaths?.includes(
                        location?.pathname?.toString()?.toLowerCase()
                      )
                        ? 'report-submenu-layout'
                        : ''
                    } ${
                      utils?.airaPagesPaths?.includes(
                        location?.pathname?.toString()?.toLowerCase()
                      )
                        ? 'aira-layout'
                        : ''
                    }`}
                  >
                    <TitleBar msalInstance={msalInstance} />
                    <MenuLayout />
                    <div
                      className={`bodypage ${
                        getSelectedMenuData?.projectType
                          ?.toString()
                          ?.toLowerCase() ===
                          utils?.airaNames?.airaName
                            ?.toString()
                            ?.toLowerCase() || isAiraLayoutChanges
                          ? utils?.airaNames?.nonNPS?.toString()?.toLowerCase()
                          : ''
                      }`}
                    >
                      <div
                        className={`body-data-page ${
                          getSelectedMenuData?.projectType
                            ?.toString()
                            ?.toLowerCase() ===
                            utils?.airaNames?.airaName
                              ?.toString()
                              ?.toLowerCase() || isAiraLayoutChanges
                            ? utils?.airaNames?.nonNPS
                                ?.toString()
                                ?.toLowerCase()
                            : ''
                        }`}
                      >
                        <RoutesFile />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                (permittedUser !== null || runtimeTokenExpired) && (
                  <UserPermission
                    accountPermissionHint={accountPermissionHint}
                    msalInstance={msalInstance}
                  />
                )
              )}
            </>
          </AuthenticatedTemplate>
        </MsalAuthenticationTemplate>
      </ConfirmProvider>
    </ErrorBoundary>
  )
}

export default App
